import React from 'react';
import {
  Box,
  Container,
  Divider,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';

const LAST_UPDATED = '20 June 2026';
const CONTACT_EMAIL = 'help@insitechinternational.com';

const SECTIONS = [
  {
    id: 'terms',
    title: 'Terms of Use',
    icon: <GavelIcon />,
    colorKey: 'primary',
    items: [
      {
        subtitle: 'Who we are',
        text: `Qode Clarity is a learning platform that helps people build practical IT skills across
Python, SQL, data analysis, Azure, and web development. The platform is operated by
Insitech International. By creating an account and using Qode Clarity you agree to these terms.`,
      },
      {
        subtitle: 'Your account',
        text: `You are responsible for keeping your password secure and for all activity that
happens under your account. If you believe your account has been compromised, contact us
immediately at ${CONTACT_EMAIL}. You must be at least 13 years old to register.`,
      },
      {
        subtitle: 'Acceptable use',
        text: `Use the platform to learn. Do not attempt to scrape content at scale, reverse-engineer
the platform, share account credentials, or use automated tools to complete lessons.
We reserve the right to suspend accounts that violate these terms.`,
      },
      {
        subtitle: 'Intellectual property',
        text: `All lesson content, question bank material, frameworks (SOLVE and 5-HOW), and platform
code are owned by Insitech International and are protected by copyright.
Your own learning notes and submitted code remain yours.`,
      },
      {
        subtitle: 'Availability',
        text: `We do our best to keep the platform available but cannot guarantee uninterrupted access.
We may change, suspend, or remove features at any time. Where possible we will give
notice of significant changes.`,
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    icon: <PolicyIcon />,
    colorKey: 'secondary',
    items: [
      {
        subtitle: 'What we collect',
        text: `We collect your name, email address, and password (hashed — never stored in plain text)
when you register. As you use the platform we record your lesson progress, completed tasks,
notes, and confidence ratings so we can show you your progress and power the review queue.`,
      },
      {
        subtitle: 'What we do not collect',
        text: `We do not collect payment information (there is no paid tier at this time), sell your data
to third parties, or use your learning data for advertising. We do not use tracking cookies
beyond the session cookie needed to keep you logged in.`,
      },
      {
        subtitle: 'How we use your data',
        text: `Your data is used to: keep you logged in, show your progress, calculate your streak,
personalise your nav to your chosen learning paths, and send you notification emails if
you opt in. We may use anonymised, aggregated usage data to improve content quality.`,
      },
      {
        subtitle: 'Email communications',
        text: `If you register, we may send you a welcome email and occasional learning reminders.
You can stop receiving these emails at any time by contacting us at ${CONTACT_EMAIL}.
We do not send marketing emails to users who have not registered.`,
      },
      {
        subtitle: 'Your rights',
        text: `You can request a copy of the data we hold about you, ask us to correct it, or ask us
to delete your account and all associated data. Email ${CONTACT_EMAIL} and we will
respond within 30 days.`,
      },
      {
        subtitle: 'Data storage',
        text: `Your data is stored in a PostgreSQL database hosted on Neon (cloud-hosted, EU/US regions).
Backups are encrypted. We implement standard technical measures to protect your personal data.`,
      },
    ],
  },
  {
    id: 'security',
    title: 'Security',
    icon: <SecurityIcon />,
    colorKey: 'success',
    items: [
      {
        subtitle: 'How we protect your account',
        text: `Passwords are hashed using bcrypt before storage — we never see your password.
Sessions use secure, HttpOnly cookies that cannot be read by JavaScript.
HTTPS is enforced on all connections to the platform.`,
      },
      {
        subtitle: 'Your responsibilities',
        text: `Use a strong, unique password for your Qode Clarity account. Do not share your
login credentials. Log out on shared devices. If you suspect unauthorised access,
change your password immediately and contact us.`,
      },
      {
        subtitle: 'Responsible disclosure',
        text: `If you discover a security vulnerability in the platform, please report it to
${CONTACT_EMAIL} before disclosing it publicly. We will acknowledge your report
within 5 working days and aim to resolve confirmed issues promptly.`,
      },
    ],
  },
];

const Section = ({ section }) => {
  const theme = useTheme();
  const color = theme.palette[section.colorKey]?.main ?? theme.palette.primary.main;

  return (
    <Paper
      id={section.id}
      variant="outlined"
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        borderColor: alpha(color, 0.2),
        bgcolor: alpha(color, 0.025),
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color,
            display: 'flex',
          }}
        >
          {section.icon}
        </Box>
        <Typography variant="h4" fontWeight={700} color={color}>
          {section.title}
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {section.items.map((item, idx) => (
          <Box key={item.subtitle}>
            {idx > 0 && <Divider sx={{ mb: 3, borderColor: alpha(color, 0.1) }} />}
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {item.subtitle}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {item.text}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const TermsPage = () => {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, sm: 8, md: 12 } }}>
      <Container maxWidth="lg">
        <Box textAlign="center" mb={{ xs: 6, sm: 8 }}>
          <Typography
            variant="h2"
            fontWeight={800}
            mb={2}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Terms & Privacy
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400} sx={{ maxWidth: 560, mx: 'auto' }}>
            Plain-English policies for Qode Clarity — the IT learning platform
            by Insitech International.
          </Typography>
          <Typography variant="caption" color="text.disabled" display="block" mt={1.5}>
            Last updated: {LAST_UPDATED}
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={4}>
          {SECTIONS.map((s) => <Section key={s.id} section={s} />)}
        </Box>

        <Box
          mt={6}
          p={3}
          sx={{
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Questions about these policies?{' '}
            <Typography
              component="a"
              href={`mailto:${CONTACT_EMAIL}`}
              variant="body2"
              color="primary.main"
              sx={{ fontWeight: 600 }}
            >
              {CONTACT_EMAIL}
            </Typography>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsPage;
